import { defineNuxtPlugin } from 'nuxt/app'
import { extractTrackingParams } from '../types/tracking-params'

// Extend Window interface to include our tracking params
declare global {
  interface Window {
    __PRESERVED_TRACKING_PARAMS?: Record<string, string>
  }
}

// Preserves tracking parameters in the browser URL while allowing Vue Router
// to think they don't exist (preventing SSR hydration mismatches)
export default defineNuxtPlugin({
  name: '00-preserve-tracking',
  order: -1000, // Run before everything
  enforce: 'pre',
  setup() {
    if (typeof window === 'undefined') return

    const url = new URL(window.location.href)

    // Capture tracking params from the current URL (supports regex patterns)
    const trackingParams = extractTrackingParams(url.searchParams)

    // Store each in sessionStorage for analytics tools to access
    for (const [param, value] of Object.entries(trackingParams)) {
      try {
        sessionStorage.setItem(`tracking_${param}`, value)
      } catch {
        // Ignore sessionStorage errors (e.g., private browsing)
      }
    }

    // Only intercept if we have tracking params to preserve
    if (Object.keys(trackingParams).length === 0) return

    // Store for debugging/analytics access (keep permanently for easier debugging)
    window.__PRESERVED_TRACKING_PARAMS = trackingParams

    // Log what we're preserving (useful for debugging in production too)
    // eslint-disable-next-line no-console
    console.log(
      '[tracking-preserve] Preserving params:',
      Object.keys(trackingParams).join(', '),
    )

    // Override history.replaceState to prevent param stripping
    const originalReplaceState = history.replaceState.bind(history)
    let hasIntercepted = false

    history.replaceState = function (
      state: any,
      title: string,
      url?: string | URL | null,
    ) {
      // Only process if we have a URL and haven't already fixed the main hydration
      if (url && !hasIntercepted) {
        try {
          const newUrl = new URL(url, window.location.origin)
          const currentUrl = new URL(window.location.href)

          // Check if this call is stripping our tracking params
          let isStrippingTracking = false
          const strippedTrackingParams: string[] = []
          for (const [param, value] of Object.entries(trackingParams)) {
            if (
              currentUrl.searchParams.has(param) &&
              !newUrl.searchParams.has(param)
            ) {
              isStrippingTracking = true
              strippedTrackingParams.push(param)
              // Re-add the tracking param
              newUrl.searchParams.set(param, value)
            }
          }

          if (isStrippingTracking) {
            // This is likely the Vue Router hydration sync
            hasIntercepted = true

            // eslint-disable-next-line no-console
            console.log(
              '[tracking-preserve] Prevented tracking param stripping, restored:',
              strippedTrackingParams,
              'Final URL:',
              newUrl.toString(),
            )

            // Call with restored URL
            return originalReplaceState(state, title, newUrl.toString())
          }
        } catch (e) {
          // If URL parsing fails, just proceed normally
          console.error('[tracking-preserve] Error processing URL:', e)
        }
      }

      // Call original for all other cases
      return originalReplaceState(state, title, url)
    }

    // Also handle pushState (less likely but just in case)
    const originalPushState = history.pushState.bind(history)

    history.pushState = function (
      state: any,
      title: string,
      url?: string | URL | null,
    ) {
      if (url) {
        try {
          const newUrl = new URL(url, window.location.origin)

          // For pushState, just ensure tracking params are included
          let needsTracking = false
          for (const [param, value] of Object.entries(trackingParams)) {
            if (!newUrl.searchParams.has(param)) {
              newUrl.searchParams.set(param, value)
              needsTracking = true
            }
          }

          if (needsTracking) {
            // Silently add tracking params back to any navigation
            return originalPushState(state, title, newUrl.toString())
          }
        } catch {
          // Ignore errors
        }
      }

      return originalPushState(state, title, url)
    }
  },
})
